import { useState, useEffect, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import apiService from '../../services/api';
import versionService from '../../services/versionService';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logging/logger';
import { sharedFetch, ATTENDANCE_TODAY_KEY } from '../../services/sharedDashboardFetch';
import {
  isDeploymentRelatedError,
  getSessionStatus,
  getCheckedInTimeLabel,
  extractWeekNumber,
  getTimeRangeLabel,
  isCheckInWindowActive,
  isLiveSessionActive,
  getVirtualTimeRangeLabel,
  getVirtualSessionWindowStatus,
  getPrimaryStatus,
  getCheckinDisplay
} from './activeAttendanceSessionsHelpers';

// Tags the attendance:checked-in event this widget dispatches so its own
// listener can ignore it. window.dispatchEvent delivers to every listener
// including this hook's, and handleCheckIn already refreshes before
// dispatching — without this tag that broadcast bounced straight back and
// fired a second, redundant fetch on every check-in.
const CHECK_IN_EVENT_SOURCE = 'active-attendance-sessions';

export default function useActiveAttendanceSessions() {
  const { user } = useAuth();
  // Depend on id/role, not the user object reference -- AuthContext hands out
  // a new object (same underlying user) on every token refresh, which was
  // re-triggering this effect and causing duplicate fetches on load.
  const userId = user?.id;
  const userRole = user?.role;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingIn, setCheckingIn] = useState(new Set());
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const deploymentErrorCountRef = useRef(0);
  const pollingPausedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Check if an update is available when deployment-related errors occur
  const checkForUpdate = useCallback(async () => {
    try {
      const hasUpdate = await versionService.checkForUpdates();
      if (hasUpdate) {
        setUpdateAvailable(true);
        pollingPausedRef.current = true; // Stop polling when update is detected
        logger.info('App update detected during attendance fetch - pausing polling');
      }
      return hasUpdate;
    } catch (e) {
      logger.warn('Failed to check for updates', { error: e });
      return false;
    }
  }, []);

  // `allowShared` is opt-in and only the mount fetch passes it. The global
  // check-in prompt requests this same endpoint at the same moment on a full
  // page load, so sharing that one in-flight request drops the duplicate.
  // Polls, focus refreshes and especially the post-check-in refresh must NOT
  // share: joining a request that started before the check-in would read back
  // state that doesn't include it.
  const fetchActiveSessions = useCallback(async ({ allowShared = false } = {}) => {
    // Don't fetch if user is null or not a student
    if (!userId || userRole !== 'student') {
      setSessions([]);
      setInitialLoadDone(true);
      return;
    }

    // Don't fetch if polling is paused due to detected update
    if (pollingPausedRef.current) {
      logger.debug('Polling paused - update available');
      return;
    }

    // The focus and visibilitychange listeners below both fire when the tab
    // regains focus, and the periodic interval can land at the same moment -
    // without this guard that fires off duplicate concurrent requests, each
    // replacing `sessions` with a fresh array and re-rendering the cards.
    if (isFetchingRef.current) {
      logger.debug('Fetch already in flight - skipping duplicate trigger');
      return;
    }
    isFetchingRef.current = true;

    try {
      setLoading(true);
      setError('');

      // Always add cache-busting parameter to ensure fresh data
      const endpoint = '/attendance/active-today?v=' + Date.now();
      const response = allowShared
        ? await sharedFetch(ATTENDANCE_TODAY_KEY, () => apiService.request(endpoint))
        : await apiService.request(endpoint);

      console.log('📍 Active Sessions API Response:', response);

      if (response.success) {
        setSessions(response.sessions || []);
        console.log('📍 Sessions set:', response.sessions?.length || 0, 'sessions');
        // Reset error count on successful fetch
        deploymentErrorCountRef.current = 0;
      } else {
        setError(response.message || 'Failed to fetch active sessions');
        console.error('📍 API Error:', response.message);
      }
    } catch (error) {
      // Handle permission errors gracefully - don't show error for non-students
      if (error.status === 403) {
        logger.debug('User does not have permission to access attendance sessions');
        setSessions([]);
        setError('');
        return;
      }

      // Check if this might be a deployment-related error
      if (isDeploymentRelatedError(error)) {
        deploymentErrorCountRef.current += 1;
        logger.warn('Possible deployment-related error in attendance fetch', {
          errorCount: deploymentErrorCountRef.current,
          error: error.message
        });

        // After 2 consecutive deployment-like errors, check for updates
        if (deploymentErrorCountRef.current >= 2) {
          const hasUpdate = await checkForUpdate();
          if (hasUpdate) {
            setError('');
            return; // Don't show error, update banner will be shown
          }
        }

        // Show a more helpful error message for deployment scenarios
        if (deploymentErrorCountRef.current >= 3) {
          setError('Connection interrupted. The app may be updating - please refresh the page.');
        } else {
          // Don't show error for first couple of failures - might be transient
          setError('');
        }
      } else {
        setError('Failed to fetch active sessions. Please try again.');
        deploymentErrorCountRef.current = 0;
      }
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
      isFetchingRef.current = false;
    }
  }, [userId, userRole, checkForUpdate]);


  useEffect(() => {
    if (userId) {
      // Always fetch fresh data when component mounts
      fetchActiveSessions({ allowShared: true });

      // Refresh when user returns to the tab (focus event)
      const handleFocus = () => {
        logger.debug('Page focused - refreshing sessions');
        fetchActiveSessions();
      };

      // Refresh when page becomes visible (visibilitychange event)
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          logger.debug('Page visible - refreshing sessions');
          fetchActiveSessions();
        }
      };

      // Periodic refresh every 2 minutes to catch admin updates
      const interval = setInterval(() => {
        logger.debug('Periodic refresh - checking for session updates');
        fetchActiveSessions();
      }, 120000); // 2 minutes

      // Refresh immediately when a check-in completes elsewhere (e.g. the global
      // check-in reminder modal) instead of waiting up to 2 minutes for the next poll.
      const handleCheckedInElsewhere = (event) => {
        if (event?.detail?.source === CHECK_IN_EVENT_SOURCE) {
          // Our own check-in already refreshed before dispatching.
          return;
        }
        logger.debug('Check-in completed elsewhere - refreshing sessions');
        fetchActiveSessions();
      };

      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('attendance:checked-in', handleCheckedInElsewhere);

      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('attendance:checked-in', handleCheckedInElsewhere);
      };
    } else {
      // Clear sessions when user is null
      setSessions([]);
    }
  }, [userId, fetchActiveSessions]);



  // Filter sessions to show until check-in window ends
  const getVisibleSessions = () => {
    if (!sessions || sessions.length === 0) {
      console.log('📍 No sessions available');
      return [];
    }

    const now = DateTime.now().setZone('America/Chicago');
    console.log('📍 Current time:', now.toISO());
    console.log('📍 Total sessions:', sessions.length);

    const visibleSessions = sessions.filter(session => {
      if (!session.end_time) {
        console.log('📍 Session filtered (no end_time):', session.session_id);
        return false;
      }

      // Parse the end_time to determine when check-in closes
      let endTime;
      try {
        if (typeof session.end_time === 'string') {
          endTime = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
        } else {
          endTime = DateTime.fromJSDate(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
        }
      } catch (e) {
        console.log('📍 Session filtered (parse error):', session.session_id, e.message);
        return false;
      }

      if (!endTime.isValid) {
        console.log('📍 Session filtered (invalid endTime):', session.session_id);
        return false;
      }

      const isVisible = now <= endTime;
      console.log('📍 Session:', session.session_id, 'end_time:', endTime.toISO(), 'visible:', isVisible);
      return isVisible;
    });

    const uniqueVisibleSessions = Array.from(
      new Map(visibleSessions.map(session => [
        `${session.entity_type || 'session'}-${session.session_id}`,
        session
      ])).values()
    );

    console.log('📍 Visible sessions:', uniqueVisibleSessions.length);
    return uniqueVisibleSessions;
  };

  const handleCheckIn = async (sessionId) => {
    try {
      setCheckingIn(prev => new Set(prev).add(sessionId));
      setError('');
      setSuccess('');

      const response = await apiService.checkInAttendance(sessionId, 'present');

      if (response.success) {
        setSuccess('Successfully checked in for attendance!');

        // Refresh sessions to get the latest data from the server
        logger.debug('Check-in successful - refreshing sessions');
        await fetchActiveSessions();

        // Let the global check-in reminder (a separate, independently polling
        // component) know right away, in case it's still queued for this session.
        window.dispatchEvent(new CustomEvent('attendance:checked-in', {
          detail: { sessionId, source: CHECK_IN_EVENT_SOURCE },
        }));

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Use the specific error message from the API response
        setError(response.message || response.error || 'Failed to check in for attendance');
      }
    } catch (error) {
      logger.error('Error checking in', { error, sessionId });
      // Try to extract meaningful error message from the error response
      let errorMessage = 'Failed to check in for attendance. Please try again.';

      // First, try to get message from response object
      if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.response?.error) {
        errorMessage = error.response.error;
      } else if (error?.message) {
        // If the message looks like JSON, try to parse it
        if (typeof error.message === 'string' && error.message.includes('{')) {
          try {
            const parsed = JSON.parse(error.message);
            errorMessage = parsed.message || parsed.error || error.message;
          } catch (e) {
            // If parsing fails, use the original message
            errorMessage = error.message;
          }
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setCheckingIn(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const handleJoinVirtualSession = (session, event) => {
    const status = getVirtualSessionWindowStatus(session);
    if (status.type !== 'available') {
      event.preventDefault();
      setSuccess('');
      setError(status.message);
      return;
    }
    // Within window - clear any previous error and let the <a> navigate normally.
    setError('');
  };

  return {
    sessions,
    loading,
    initialLoadDone,
    error,
    success,
    checkingIn,
    updateAvailable,
    getVisibleSessions,
    handleCheckIn,
    handleJoinVirtualSession,
    getSessionStatus,
    getCheckedInTimeLabel,
    extractWeekNumber,
    getTimeRangeLabel,
    isCheckInWindowActive,
    isLiveSessionActive,
    getVirtualTimeRangeLabel,
    getPrimaryStatus,
    getCheckinDisplay
  };
}
